import { useEffect, useMemo, useState } from 'react';
import { crmApi, CrmRoleNode } from '@/services/api';

// Selector de roles funcionales (árbol «Roles VIDA y LIVRE», VIDA-354).
// Clasifica a la persona; no da permisos en el panel.
export default function RolesPicker({ value, onChange }: { value: string[]; onChange: (roles: string[]) => void }) {
  const [nodes, setNodes] = useState<CrmRoleNode[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    crmApi.getRolesTaxonomy()
      .then((r) => setNodes(r.data.roles || []))
      .catch(() => setNodes([]));
  }, []);

  const groups = useMemo(() => {
    const list = nodes || [];
    return list
      .filter((n) => n.depth === 0)
      .map((g) => ({ ...g, children: list.filter((n) => n.parent === g.id) }));
  }, [nodes]);

  const byId = useMemo(() => new Map((nodes || []).map((n) => [n.id, n])), [nodes]);

  // Roles que ya no se pueden asignar a contactos (p. ej. los internos de VIDA):
  // se avisan y se quitan, para que guardar la ficha no falle.
  const [removed, setRemoved] = useState<string[]>([]);
  useEffect(() => {
    if (!nodes || nodes.length === 0) return;
    const stale = value.filter((id) => !byId.has(id));
    if (stale.length === 0) return;
    setRemoved(stale);
    onChange(value.filter((id) => byId.has(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((r) => r !== id) : [...value, id]);

  if (nodes === null) return <p className="text-xs text-gray-400">Cargando roles…</p>;
  if (nodes.length === 0) return <p className="text-xs text-gray-400">El árbol de roles todavía no está cargado.</p>;

  return (
    <div className="space-y-2">
      {removed.length > 0 && (
        <p className="text-xs text-amber-700">Se quitarán al guardar: {removed.map((id) => id.toUpperCase()).join(', ')} (roles internos de VIDA, no se asignan a contactos).</p>
      )}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((id) => (
            <button key={id} type="button" onClick={() => toggle(id)} title="Quitar"
              className="px-2 py-0.5 rounded-full text-xs bg-vida-100 text-vida-800 hover:bg-vida-200">
              {byId.get(id)?.code} {byId.get(id)?.name ?? id} ×
            </button>
          ))}
        </div>
      )}
      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
        {groups.map((g) => {
          const selected = g.children.filter((c) => value.includes(c.id)).length;
          return (
            <div key={g.id}>
              <button type="button" onClick={() => setOpen(open === g.id ? null : g.id)} aria-expanded={open === g.id}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50">
                <span><span className="font-semibold">{g.code}</span> · {g.name}</span>
                <span className="text-xs text-gray-400">{selected > 0 ? `${selected} elegido${selected > 1 ? 's' : ''}` : ''} {open === g.id ? '▲' : '▼'}</span>
              </button>
              {open === g.id && (
                <div className="px-3 pb-2 flex flex-wrap gap-1">
                  {g.children.map((c) => {
                    const on = value.includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => toggle(c.id)} aria-pressed={on}
                        className={`px-2 py-1 rounded-full text-xs border ${on ? 'bg-vida-600 text-white border-vida-600' : 'bg-white text-gray-700 border-gray-300 hover:border-vida-400'}`}>
                        {c.code} {c.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
